import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Send, Paperclip, Square, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { OfflineIndicator } from '@/components/OfflineIndicator';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onCancelStream?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  isWaitingForResponse?: boolean;
  placeholder?: string;
  className?: string;
  error?: string | null;
  onRetry?: () => void;
  sessionState: 'none' | 'active';
  onCreateSessionWithMessage?: (message: string) => Promise<void>;
  initialValue?: string;
}

export function ChatInput({
  onSendMessage,
  onCancelStream,
  disabled = false,
  isStreaming = false,
  isWaitingForResponse = false,
  placeholder,
  className,
  error,
  onRetry,
  sessionState,
  onCreateSessionWithMessage,
  initialValue = ''
}: ChatInputProps) {
  const [message, setMessage] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const networkStatus = useNetworkStatus();
  const { isMobile, isSmallMobile } = useBreakpoint();

  // Update message when initialValue changes (for error recovery)
  useEffect(() => {
    if (initialValue && initialValue !== message) {
      setMessage(initialValue);
    }
  }, [initialValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check network status before sending
    if (!networkStatus.isOnline) {
      return;
    }

    if (message.trim() && !disabled) {
      const messageText = message.trim();
      
      // Call appropriate handler based on session state
      if (sessionState === 'none' && onCreateSessionWithMessage) {
        // Don't clear message immediately for session creation - let parent handle it
        try {
          await onCreateSessionWithMessage(messageText);
          // Only clear message on successful session creation
          setMessage('');
          // Reset textarea height
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }
        } catch (error) {
          // Keep message in input for retry - don't clear it
          console.error('Session creation failed:', error);
        }
      } else {
        // For regular message sending, clear immediately
        setMessage('');
        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
        onSendMessage(messageText);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea with mobile-specific max height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = isMobile ? 80 : 120;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, [message, isMobile]);

  const isInputDisabled = disabled || !networkStatus.isOnline;

  // Dynamic placeholder text based on session state
  const getPlaceholderText = () => {
    if (!networkStatus.isOnline) {
      return "You are offline...";
    }
    
    if (placeholder) {
      return placeholder;
    }
    
    return sessionState === 'none' 
      ? "Type a message to start a new conversation..."
      : "Ask a question about your warrant documents...";
  };

  return (
    <div className={cn(
      'border-t bg-background',
      sessionState === 'none' && 'bg-background/95 backdrop-blur-sm',
      isMobile && 'mobile-chat-input-area',
      !isMobile && 'chat-input-container',
      className
    )}>
      {/* Error display */}
      {error && (
        <div className={cn(
          'max-w-4xl mx-auto pt-2',
          isMobile ? 'px-2' : 'px-4'
        )}>
          <div className={cn(
            'flex items-center gap-2 p-2 text-destructive bg-destructive/10 rounded-md border border-destructive/20',
            isMobile ? 'text-xs' : 'text-sm'
          )}>
            <AlertCircle className={cn(
              'flex-shrink-0',
              isMobile ? 'h-3 w-3' : 'h-4 w-4'
            )} />
            <span className="flex-1">
              {sessionState === 'none' 
                ? `Failed to start conversation: ${error}` 
                : error
              }
            </span>
            {onRetry && (
              <Button
                onClick={onRetry}
                size="sm"
                variant="outline"
                disabled={!message.trim() || disabled}
                className={cn(
                  'text-xs border-destructive/30 hover:bg-destructive/10',
                  isMobile ? 'h-5' : 'h-6'
                )}
              >
                {sessionState === 'none' ? 'Retry' : 'Retry'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Offline indicator */}
      {!networkStatus.isOnline && (
        <div className={cn(
          'max-w-4xl mx-auto pt-2',
          isMobile ? 'px-2' : 'px-4'
        )}>
          <OfflineIndicator variant="alert" />
        </div>
      )}

      <div className={cn(
        'max-w-4xl mx-auto',
        isMobile ? 'mobile-input-wrapper' : 'p-4'
      )}>
        <form onSubmit={handleSubmit} className={cn(
          'flex items-start',
          isMobile ? 'gap-1' : 'gap-2'
        )}>
          {/* File attachment button (placeholder for future) - hide on small mobile */}
          {!isSmallMobile && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'flex-shrink-0 mt-1',
                isMobile ? 'h-8 w-8' : 'h-10 w-10'
              )}
              disabled
            >
              <Paperclip className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            </Button>
          )}

          {/* Message input with enhanced styling for welcome state */}
          <div className="flex-1 relative flex flex-col">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholderText()}
              disabled={isInputDisabled}
              style={{ verticalAlign: 'bottom' }}
              className={cn(
                'w-full resize-none rounded-md border bg-background transition-all duration-200',
                'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
                'disabled:opacity-50',
                // Enhanced styling for welcome state
                sessionState === 'none' && [
                  'border-primary/30 shadow-sm',
                  'focus-visible:border-primary/50 focus-visible:shadow-md',
                  'hover:border-primary/40'
                ],
                sessionState === 'active' && 'border-input',
                isMobile ? 'px-2 py-1.5 text-sm min-h-[36px] max-h-[80px]' : 'px-3 py-2 text-sm min-h-[40px] max-h-[120px]'
              )}
              rows={1}
            />
            
            {/* Loading overlay for session creation */}
            {sessionState === 'none' && isWaitingForResponse && (
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-md flex items-center justify-center">
                <div className="flex items-center gap-2 text-primary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs font-medium">Starting conversation...</span>
                </div>
              </div>
            )}
          </div>

          {/* Send/Cancel button with enhanced styling */}
          {isStreaming ? (
            <Button
              type="button"
              size="icon"
              variant="destructive"
              onClick={onCancelStream}
              className={cn(
                'flex-shrink-0 mt-1',
                isMobile ? 'h-8 w-8' : 'h-10 w-10'
              )}
            >
              <Square className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              disabled={isInputDisabled || !message.trim() || isWaitingForResponse}
              className={cn(
                'flex-shrink-0 mt-1 transition-all duration-200',
                // Enhanced styling for welcome state
                sessionState === 'none' && [
                  'bg-primary hover:bg-primary/90 shadow-md',
                  'disabled:bg-muted disabled:shadow-none'
                ],
                isMobile ? 'h-8 w-8' : 'h-10 w-10'
              )}
            >
              {isWaitingForResponse ? (
                <Loader2 className={cn(
                  'animate-spin',
                  isMobile ? 'h-3 w-3' : 'h-4 w-4'
                )} />
              ) : (
                <Send className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
              )}
            </Button>
          )}
        </form>

        {/* Enhanced helper text */}
        <div className={cn(
          'flex items-center justify-between text-muted-foreground',
          isMobile ? 'mt-1 text-xs' : 'mt-2 text-xs'
        )}>
          <span className="flex-1 min-w-0">
            {!networkStatus.isOnline
              ? "You are offline"
              : sessionState === 'none'
                ? isSmallMobile
                  ? "Enter to start chat"
                  : "Press Enter to start a new conversation, Shift+Enter for new line"
                : isSmallMobile
                  ? "Enter to send"
                  : "Press Enter to send, Shift+Enter for new line"
            }
          </span>
          <div className="flex-shrink-0 ml-2">
            {isStreaming && (
              <span className="text-blue-600 font-medium">
                {isSmallMobile ? "Streaming..." : "Streaming response... Click stop to cancel"}
              </span>
            )}
            {isWaitingForResponse && (
              <span className={cn(
                "font-medium",
                sessionState === 'none' ? "text-primary" : "text-blue-600"
              )}>
                {sessionState === 'none' 
                  ? (isSmallMobile ? "Starting..." : "Starting conversation...")
                  : (isSmallMobile ? "Sending..." : "Sending message...")
                }
              </span>
            )}
            {disabled && !isStreaming && !isWaitingForResponse && networkStatus.isOnline && (
              <span className="text-orange-600">
                {isSmallMobile ? "Waiting..." : "Waiting for response..."}
              </span>
            )}
            {networkStatus.isSlowConnection && networkStatus.isOnline && (
              <span className="text-orange-600">
                {isSmallMobile ? "Slow connection" : "Slow connection detected"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}